import { h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { UI_FORCE_VISIBILITY } from '../../../../store/types';
import { computeVideoTitle } from '../../../../utils/videoMetaUtils';
import { DIALOG_LAYER } from '../../../common/constants';
import { useNewStore, usePlayerContext, useStyles } from '../../../common/hooks';
import { withBreakPoints } from '../../hoc';
import { LIVE_FILL_COLOR, OVERLAY_COLOR_OPAQUE, PRIMARY_FILL_COLOR, PRIMARY_TEXT_COLOR } from '../../theme/colors';
import { Label, Titles } from '../common';
import { TIMESHIFTING_TYPE_MAP } from '../controls/playback/const';
import {
  HEADER_WRAPPER,
  MAIN_TITLE_TRANSITION,
  TITLE_WRAPPER
} from './styles';
import ViewerLiveInfo from './ViewerLiveInfo';

const TITLE_SWITCH_DELAY = 2000;
const COMING_NEXT_DELAY = 2300;

function HeaderInfos({
  containerVisible,
  large,
  small,
  extraSmall,
  hidden,
  mobile,
  medium
}) {
  const player = usePlayerContext();
  const { showTitle, forceShowTitle, isFullscreen, originUrl, showViewers, displayedViewers,
    liveLabel, pubLabel, seekBackground } = useNewStore('ui', player);
  const {
    isLive, isDVR, numberViewers,
    timeshifting: { canGoBackToLive, type: timeshifting },
    videoAdditionalTitle, videoTitle, videoPreTitle, subTitle, isTv
  } = useNewStore('media', player);
  const { showCountdown, isAd } = useNewStore('ad', player);

  const isEmbed = Boolean(originUrl);
  const transition = useRef(null);
  const [switchTitles, setSwitchTitles] = useState(false);
  const setStyles = useStyles();

  const { computedTitle, computedSubtitle } = useMemo(
    () => computeVideoTitle(videoTitle, subTitle, videoPreTitle, videoAdditionalTitle, seekBackground),
    [videoTitle, subTitle, videoPreTitle, videoAdditionalTitle, seekBackground]
  );

  useEffect(() => {
    if (!transition.current && containerVisible && !isAd) {
      transition.current = setTimeout(
        () => {
          setSwitchTitles(true);
          transition.current = null;
        },
        TITLE_SWITCH_DELAY
      );

      player.store.dispatch({
        type: UI_FORCE_VISIBILITY,
        payload: {
          forceVisible: !mobile
        } /* don't force visibility on mobile */
      });
    }
  }, [containerVisible]);

  useEffect(() => switchTitles && setTimeout(() => {
    setSwitchTitles(false);
    player.store.dispatch({
      type: UI_FORCE_VISIBILITY,
      payload: { forceVisible: false }
    });
  }, COMING_NEXT_DELAY), [switchTitles]);

  const showLiveLabel = isLive && (!pubLabel || !isAd);
  const showViewersInfo = isLive && showViewers && numberViewers && displayedViewers && isLive && !isAd;

  const shouldShowInfo = !isAd
    && showTitle
    && !showCountdown
    && (isEmbed || isDVR || isFullscreen || isTv || forceShowTitle);

  if (hidden || (!showLiveLabel && !showViewersInfo && !shouldShowInfo)) return null;

  const LABEL_VIEWERS_BLOCK_STYLE = { display: 'flex', zIndex: DIALOG_LAYER };

  return (
    <div style={HEADER_WRAPPER}>
      {(showLiveLabel || showViewersInfo) && (
        <div className="ftv-magnetoscope--live-label" style={{ flexBasis: 'content' }}>

          <div style={LABEL_VIEWERS_BLOCK_STYLE}>
            {showLiveLabel && (
              <Label
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (canGoBackToLive) player.backToLive(TIMESHIFTING_TYPE_MAP[timeshifting]);
                }}
                message={liveLabel}
                style={{
                  pointerEvents: 'auto',
                  ...(!canGoBackToLive
                    ? { backgroundColor: LIVE_FILL_COLOR, color: PRIMARY_TEXT_COLOR }
                    : { backgroundColor: PRIMARY_FILL_COLOR, color: OVERLAY_COLOR_OPAQUE, cursor: 'pointer' })
                }}
              />
            )}

            {showViewersInfo && (
              <ViewerLiveInfo
                connected={numberViewers}
                isFullscreen={isFullscreen}
                mobile={mobile}
                extraSmall={extraSmall}
                medium={medium}
                small={small}
                large={large}
                size={setStyles({
                  'small+extraSmall': { size: 20 },
                  medium: { size: 24 },
                  'large+extraLarge': { size: 30 }
                }).size}
              />
            )}
          </div>
        </div>
      )}
      {shouldShowInfo && (
        <div style={TITLE_WRAPPER}>
          <div style={{ opacity: 1, ...MAIN_TITLE_TRANSITION }}>
            <Titles
              title={computedTitle}
              subTitle={computedSubtitle}
              titleStyle={setStyles({
                extraSmall: { fontSize: '16px', fontWeight: 700, marginBottom: '4px' }
              })}
              subTitleStyle={setStyles({
                extraSmall: { fontSize: '12px', fontWeight: 400 }
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default withBreakPoints(HeaderInfos);
